import * as React from 'react';
import {
  root as rootCls,
  handle as handleCls,
  edgeEnd as edgeEndCls,
  edgeStart as edgeStartCls,
} from './ResizablePane.css';

/** Keyboard resize increment, in px. */
const STEP = 16;

export interface ResizablePaneProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Which edge of the pane carries the drag handle: `end` for a left-hand panel
   * (drag right to widen), `start` for a right-hand one (drag left to widen).
   */
  edge: 'start' | 'end';
  /** Accessible name of the handle, e.g. "Resize workstreams panel". */
  handleLabel: string;
  /** Narrowest width, px. Also the CSS `min-width`. */
  min: number;
  /** Widest width, px. Also the CSS `max-width`. */
  max: number;
  /** Width before the first drag — px or any CSS length (`'40%'`). Default `min`. */
  defaultWidth?: number | string;
  /**
   * Px to keep free in the pane's parent for its siblings, so dragging can't
   * squeeze them out. The pane's ceiling becomes `min(max, parent − reserve)`.
   * Omit when `max` alone is enough.
   */
  reserve?: number;
  /**
   * Controlled width, px. Omit to let the pane keep its own. Lift it when
   * several panes occupy the same slot and should share one width.
   */
  width?: number;
  /** Fires on every step of a drag — live width. */
  onWidthChange?: (width: number) => void;
  /** Fires once when a drag ends (or after each arrow-key step) — the place to persist. */
  onResizeEnd?: (width: number) => void;
  /**
   * Fill the parent and drop the handle — for when the panel is a full-pane view
   * and there is nothing to resize against. The panel keeps its place in the tree,
   * so toggling this never remounts it (unlike conditionally wrapping it).
   */
  disabled?: boolean;
  /** The panel to resize — a single element that should fill the pane's width. */
  children: React.ReactElement;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * ResizablePane — wraps one shell panel in a column the user can widen or
 * narrow by dragging (pointer, incl. touch) or with ←/→ on the focused handle
 * (the ARIA window-splitter pattern). Width starts at `defaultWidth`, is
 * uncontrolled unless `width` is passed, and is not persisted (see `onResizeEnd`).
 */
export function ResizablePane({
  edge,
  handleLabel,
  min,
  max,
  defaultWidth = min,
  reserve,
  width: controlledWidth,
  onWidthChange,
  onResizeEnd,
  disabled,
  children,
  ref,
  className,
  style,
  ...rest
}: ResizablePaneProps) {
  const [ownWidth, setOwnWidth] = React.useState<number>();
  const width = controlledWidth ?? ownWidth;
  const drag = React.useRef<{ x: number; width: number; last?: number } | null>(null);
  // Widening moves the handle away from the pane's body: right for `end`, left for `start`.
  const dir = edge === 'end' ? 1 : -1;

  function resize(handle: HTMLElement, px: number): number {
    const parent = handle.parentElement?.parentElement;
    const cap = reserve !== undefined && parent ? parent.clientWidth - reserve : max;
    // `min` wins over the cap: a cramped viewport never shrinks the pane below its floor.
    const next = Math.round(Math.max(min, Math.min(max, cap, px)));
    setOwnWidth(next);
    onWidthChange?.(next);
    return next;
  }

  function endDrag() {
    if (drag.current?.last !== undefined) onResizeEnd?.(drag.current.last);
    drag.current = null;
  }

  const paneWidth = (h: HTMLElement) => h.parentElement!.getBoundingClientRect().width;

  // Before the first resize the width is `defaultWidth`, which may be a CSS length
  // ('40%'), so read it off the DOM — a focusable separator needs `aria-valuenow`.
  // ponytail: measured on mount + focus, not live; a ResizeObserver if a window
  // resize ever needs announcing before the handle is focused.
  const handleRef = React.useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = React.useState<number>();
  const measure = () => {
    if (handleRef.current) setMeasured(Math.round(paneWidth(handleRef.current)));
  };
  React.useLayoutEffect(measure, [disabled]); // the handle mounts late when re-enabled
  const valueNow = width ?? measured;

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    drag.current = { x: e.clientX, width: paneWidth(e.currentTarget) };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    // Focus is not moved by a prevented pointerdown; and it stops text selection mid-drag.
    e.preventDefault();
    e.currentTarget.focus();
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (drag.current)
      drag.current.last = resize(
        e.currentTarget,
        drag.current.width + dir * (e.clientX - drag.current.x),
      );
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const widen = (e.key === 'ArrowRight' ? 1 : -1) * dir;
    // Not `onResizeEnd?.(resize(...))`: an absent callback would skip the resize itself.
    const next = resize(e.currentTarget, paneWidth(e.currentTarget) + widen * STEP);
    onResizeEnd?.(next);
  }

  return (
    <div
      ref={ref}
      className={[rootCls, className].filter(Boolean).join(' ')}
      style={{
        ...style,
        ...(disabled
          ? { width: '100%', minWidth: 0, maxWidth: 'none' }
          : {
              width: width ?? defaultWidth,
              minWidth: min,
              // With `reserve`, a stored/controlled width can't exceed the room the
              // siblings need, and that holds live as the window shrinks. (`min` wins.)
              maxWidth: reserve === undefined ? max : `min(${max}px, calc(100% - ${reserve}px))`,
            }),
      }}
      {...rest}
    >
      {children}
      {disabled ? null : (
        <>
          {/* A focusable `separator` is an interactive widget (ARIA 1.2 window splitter);
          jsx-a11y only knows `separator` as static. */}
          {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={handleLabel}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={
              valueNow === undefined ? undefined : Math.min(max, Math.max(min, valueNow))
            }
            tabIndex={0}
            ref={handleRef}
            onFocus={measure}
            className={[handleCls, edge === 'end' ? edgeEndCls : edgeStartCls].join(' ')}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onKeyDown={onKeyDown}
          />
          {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        </>
      )}
    </div>
  );
}
ResizablePane.displayName = 'ResizablePane';
